import { HIGH_SCHOOL_YEARS_LIST, STUDENT_SCOPE } from 'constants/gradeYears';

import Api from 'core/api';

import getAlphaLastNameFilter from 'selectors/filters/getAlphaLastNameFilter';
import getAlumniClassYearsFilter from 'selectors/filters/getAlumniClassYearsFilter';
import getBulkIdentifierMatchFilter from 'selectors/filters/getBulkIdentifierMatchFilter';
import getCaseloadFilter from 'selectors/filters/getCaseloadFilter';
import getCaseloadSourceFilter from 'selectors/filters/getCaseloadSourceFilter';
import getCaseloadTypeFilter from 'selectors/filters/getCaseloadTypeFilter';
import getClassYearsFilter from 'selectors/filters/getClassYearsFilter';
import getGradeFilter from 'selectors/filters/getGradeFilter';
import getGradeFilter6Through11Only from 'selectors/filters/getGradeFilter6Through11Only';
import getGradeFilter6Through12Only from 'selectors/filters/getGradeFilter6Through12Only';
import getGradeFilter9Through12Only from 'selectors/filters/getGradeFilter9Through12Only';
import getGradeFilterK12Only from 'selectors/filters/getGradeFilterK12Only';
import getGradeFilterKThrough5Only from 'selectors/filters/getGradeFilterKThrough5Only';
import getRestrictedSchoolFilter from 'selectors/filters/getRestrictedSchoolFilters';
import getSchoolFilter from 'selectors/filters/getSchoolFilter';

import getStudentTagFilter from 'selectors/filters/getStudentTagFilter';
import getStudentTagSourceFilter from 'selectors/filters/getStudentTagSourceFilter';
import getStudentTagTypeFilter from 'selectors/filters/getStudentTagTypeFilter';

import {
  FILTER_SCHOOLS,
  FILTER_SCHOOLS_9_THROUGH_12,
  FILTER_CASELOADS,
  FILTER_CASELOADS_9_THROUGH_12,
  FILTER_GRADES,
  FILTER_GRADES_K12_ONLY,
  FILTER_GRADES_9_THROUGH_12_ONLY,
  FILTER_GRADES_6_THROUGH_11_ONLY,
  FILTER_GRADES_6_THROUGH_12_ONLY,
  FILTER_GRADES_K_THROUGH_5_ONLY,
  FILTER_GRADES_ELEMENTARY_AND_SECONDARY,
  FILTER_K12_SCHOOL_TAGS,
  FILTER_STUDENT_TAGS,
  FILTER_STUDENT_TAGS_9_THROUGH_12,
  FILTER_STUDENT_TAGS_VERBOSE,
  FILTER_ACTIVITIES,
  FILTER_CLASSYEAR,
  FILTER_ALPHALASTNAME,
  FILTER_CASELOAD_SOURCE,
  FILTER_CASELOAD_TYPE,
  FILTER_STUDENT_TAG_SOURCE,
  FILTER_STUDENT_TAG_TYPE,
  FILTER_BULK_IDENTIFIER_MATCH,
  FILTER_CLASS_YEARS,
  FILTER_ALUMNI_CLASS_YEARS,
  FILTER_RESTRICTED_SCHOOLS,
  FILTER_RESTRICTED_SCHOOLS_ELEMENTARY_AND_SECONDARY,
} from 'modules/StudentScopeFilter/constants';
import {
  formatIdFilters,
  formatActivities,
  formatClassYear,
  formatStudentTags,
  formatAlphaLastName,
  formatBulkIdentifierMatch,
} from 'modules/StudentScopeFilter/filterSelectors';

const getCaseloadFilterContext = ({ filters = {} } = {}) => ({
  key: 'caseloads',
  source: 'caseloads',
  filterName: 'Staff rosters',
  searchable: true,
  defaultSelection: [],
  options: [],
  selector: 'id',
  label: 'name',
  reactQueryConfig: {
    queryKey: ['Api.slUsers.getK12AdminCaseloads'],
    queryFn: () => Api.slUsers.getK12AdminCaseloads(),
    enabled: true,
  },
  paginatedApiProps: {
    apiFunc: Api.roster.getCaseloadList,
    apiFuncName: 'roster.getCaseloadList',
    defaultOrderBy: 'name',
    filters: {
      fields: ['id', 'name', 'category'].join(','),
      ...filters,
    },
    selectItems: items =>
      items.map(formatItem => ({
        ...formatItem,
        subtitle: formatItem.category?.name,
        title: formatItem.name,
        value: formatItem.id,
        label: formatItem.name,
        name: formatItem.name,
        id: formatItem.id,
      })),
    useLimitOffset: true,
    usePagination: true,
  },
});

const studentTagFilterConfig = ({
  queryParamKey = 'tags',
  apiParams = {},
} = {}) => ({
  getQueryParamValue: formatStudentTags,
  getDataFromState: selected => ({
    personalTags: selected?.personalTags ?? {},
    officialTags: selected?.officialTags || selected?.studentTag || {},
  }),
  queryParamKey,
  reactQueryConfig: {
    queryKey: ['Api.roster.getStudentTagList'],
    queryFn: () =>
      Api.roster.getStudentTagList({
        params: {
          fields: ['id', 'name'].join(','),
          ...apiParams,
        },
      }),
    enabled: true,
    select: getStudentTagFilter,
  },
});

export const filterTypes = {
  [FILTER_SCHOOLS]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.schools ?? {},
    queryParamKey: 'schools',
    reactQueryConfig: {
      queryKey: ['Api.slSchools.getK12SchoolList'],
      queryFn: () =>
        Api.slSchools.getK12SchoolList({
          params: {
            fields: ['pk', 'name'].join(','),
            has_active_student: true,
          },
        }),
      enabled: true,
      select: getSchoolFilter,
    },
  },
  [FILTER_SCHOOLS_9_THROUGH_12]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.schools ?? {},
    queryParamKey: 'schools',
    reactQueryConfig: {
      queryKey: ['Api.slSchools.getK12SchoolList'],
      queryFn: () =>
        Api.slSchools.getK12SchoolList({
          params: {
            fields: ['pk', 'name'].join(','),
            has_active_student: true,
            high_schools_only: true,
          },
        }),
      enabled: true,
      select: getSchoolFilter,
    },
  },
  [FILTER_RESTRICTED_SCHOOLS]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.schools ?? {},
    queryParamKey: 'schools',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminSchools'],
      queryFn: () =>
        Api.slUsers.getK12AdminSchools({
          params: { studentScope: STUDENT_SCOPE.SECONDARY },
        }),
      enabled: true,
      select: getRestrictedSchoolFilter,
    },
  },
  [FILTER_RESTRICTED_SCHOOLS_ELEMENTARY_AND_SECONDARY]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.schools ?? {},
    queryParamKey: 'schools',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminSchools'],
      queryFn: () =>
        Api.slUsers.getK12AdminSchools({
          params: { studentScope: STUDENT_SCOPE.ELEMENTARY_AND_SECONDARY },
        }),
      enabled: true,
      select: getRestrictedSchoolFilter,
    },
  },
  [FILTER_CASELOADS]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.caseloads ?? {},
    queryParamKey: 'caseloads',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminCaseloads'],
      queryFn: () => Api.slUsers.getK12AdminCaseloads(),
      enabled: true,
      select: getCaseloadFilter,
    },
    paginated: true,
    getFilterContext: getCaseloadFilterContext,
  },
  [FILTER_CASELOADS_9_THROUGH_12]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.caseloads ?? {},
    queryParamKey: 'caseloads',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminCaseloads'],
      queryFn: () => Api.slUsers.getK12AdminCaseloads(),
      enabled: true,
      select: getCaseloadFilter,
    },
    paginated: true,
    getFilterContext: () =>
      getCaseloadFilterContext({
        filters: { grades: HIGH_SCHOOL_YEARS_LIST.join(',') },
      }),
  },
  [FILTER_GRADES]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.grades ?? {},
    queryParamKey: 'grades',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminGrades'],
      queryFn: () => Api.slUsers.getK12AdminGrades(),
      enabled: true,
      select: getGradeFilter,
    },
  },
  [FILTER_GRADES_K12_ONLY]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.grades ?? {},
    queryParamKey: 'grades',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminGrades'],
      queryFn: () => Api.slUsers.getK12AdminGrades(),
      enabled: true,
      select: getGradeFilterK12Only,
    },
  },
  [FILTER_GRADES_9_THROUGH_12_ONLY]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.grades ?? {},
    queryParamKey: 'grades',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminGrades'],
      queryFn: () => Api.slUsers.getK12AdminGrades(),
      enabled: true,
      select: getGradeFilter9Through12Only,
    },
  },
  [FILTER_GRADES_6_THROUGH_11_ONLY]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.grades ?? {},
    queryParamKey: 'grades',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminGrades'],
      queryFn: () => Api.slUsers.getK12AdminGrades(),
      enabled: true,
      select: getGradeFilter6Through11Only,
    },
  },
  [FILTER_GRADES_6_THROUGH_12_ONLY]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.grades ?? {},
    queryParamKey: 'grades',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminGrades'],
      queryFn: () =>
        Api.slUsers.getK12AdminGrades({
          params: { studentScope: STUDENT_SCOPE.SECONDARY },
        }),
      enabled: true,
      select: getGradeFilter6Through12Only,
    },
  },
  [FILTER_GRADES_K_THROUGH_5_ONLY]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.grades ?? {},
    queryParamKey: 'grades',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminGrades'],
      queryFn: () =>
        Api.slUsers.getK12AdminGrades({
          params: { studentScope: STUDENT_SCOPE.ELEMENTARY },
        }),
      enabled: true,
      select: getGradeFilterKThrough5Only,
    },
  },
  [FILTER_GRADES_ELEMENTARY_AND_SECONDARY]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.grades ?? {},
    queryParamKey: 'grades',
    reactQueryConfig: {
      queryKey: ['Api.slUsers.getK12AdminGrades'],
      queryFn: () =>
        Api.slUsers.getK12AdminGrades({
          params: { studentScope: STUDENT_SCOPE.ELEMENTARY_AND_SECONDARY },
        }),
      enabled: true,
      select: getGradeFilter,
    },
  },
  [FILTER_K12_SCHOOL_TAGS]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected =>
      Object.keys(selected)
        .filter(k => k.indexOf('k12SchoolTags') === 0)
        .reduce(
          (agg, k) => ({
            ...agg,
            ...selected[k],
          }),
          {},
        ),
    queryParamKey: 'k12_school_tags',
  },
  [FILTER_STUDENT_TAGS]: studentTagFilterConfig(),
  [FILTER_STUDENT_TAGS_9_THROUGH_12]: studentTagFilterConfig({
    apiParams: { grades: HIGH_SCHOOL_YEARS_LIST.join(',') },
  }),
  [FILTER_STUDENT_TAGS_VERBOSE]: studentTagFilterConfig({
    queryParamKey: 'student_tags',
  }),
  [FILTER_ACTIVITIES]: {
    getQueryParamValue: formatActivities,
    getDataFromState: selected => selected?.activities ?? {},
  },
  [FILTER_CLASSYEAR]: {
    getQueryParamValue: formatClassYear,
    getDataFromState: selected => selected?.classYear ?? {},
  },
  [FILTER_ALPHALASTNAME]: {
    getQueryParamValue: formatAlphaLastName,
    getDataFromState: selected => selected?.alphaLastName ?? {},
    getFilterContext: getAlphaLastNameFilter,
  },
  [FILTER_CASELOAD_TYPE]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.caseloadType ?? {},
    queryParamKey: 'category',
    reactQueryConfig: {
      queryKey: ['Api.slSchools.getCaseloadCategoryList'],
      queryFn: () => Api.slSchools.getCaseloadCategoryList(),
      enabled: true,
      select: getCaseloadTypeFilter,
    },
  },
  [FILTER_CASELOAD_SOURCE]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.caseloadSource ?? {},
    queryParamKey: 'source',
    getFilterContext: getCaseloadSourceFilter,
  },
  // currently this is same as caseload source
  [FILTER_STUDENT_TAG_SOURCE]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.studentTagSource ?? {},
    queryParamKey: 'source',
    getFilterContext: getStudentTagSourceFilter,
  },
  [FILTER_STUDENT_TAG_TYPE]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.studentTagType ?? {},
    queryParamKey: 'tag_type',
    getFilterContext: getStudentTagTypeFilter,
  },
  [FILTER_BULK_IDENTIFIER_MATCH]: {
    getQueryParamValue: formatBulkIdentifierMatch,
    getDataFromState: selected => selected?.bulkMatch ?? {},
    getFilterContext: getBulkIdentifierMatchFilter,
    queryParamKey: 'bulk_match',
  },
  [FILTER_CLASS_YEARS]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.classYears ?? {},
    queryParamKey: 'class_years',
    reactQueryConfig: {
      queryKey: ['slUsers.getK12AdminClassYears'],
      queryFn: () => Api.slUsers.getK12AdminClassYears(),
      enabled: true,
      select: getClassYearsFilter,
    },
  },
  [FILTER_ALUMNI_CLASS_YEARS]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.alumniClassYears ?? {},
    queryParamKey: 'class_years',
    reactQueryConfig: {
      queryKey: ['alumni.getAlumniFilterContext'],
      queryFn: () => Api.alumni.getAlumniFilterContext(),
      enabled: true,
      select: getAlumniClassYearsFilter,
    },
  },
};

export const defaultFilterList = [
  FILTER_SCHOOLS,
  FILTER_CASELOADS,
  FILTER_GRADES,
  FILTER_K12_SCHOOL_TAGS,
  FILTER_STUDENT_TAGS,
  FILTER_ACTIVITIES,
  FILTER_CLASSYEAR,
  FILTER_CLASS_YEARS,
];
